import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Switch,
  Divider,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
} from "@chakra-ui/react";
import { FaPlus, FaBolt, FaPen, FaTrash } from "react-icons/fa";
import { CANAUX, canal } from "variables/veille";
import { useVeilles } from "contexts/VeillesContext";
import { useSession } from "contexts/SessionContext";
import { supabase } from "lib/supabase";

// Conversion du seuil : libellé affiché (UI) <-> colonnes de la table alerts.
function seuilToDb(s) {
  const m = /(\d+)/.exec(s || "");
  if (m) return { seuil_type: "volume_horaire", seuil_valeur: parseInt(m[1], 10), fenetre_minutes: 60 };
  return { seuil_type: "toute_mention", seuil_valeur: null, fenetre_minutes: 60 };
}
function seuilFromDb(row) {
  if (row.seuil_type === "toute_mention") return "Dès la première mention";
  const unite = row.seuil_type === "volume_negatif_horaire" ? "mentions négatives par heure" : "mentions par heure";
  return `Plus de ${row.seuil_valeur || 0} ${unite}`;
}
// Le canal de notification est un enum en base (email/sms/push/slack) ; l'UI affiche des libellés.
const NOTIF_UI_TO_DB = { "E-mail": "email", SMS: "sms", Push: "push", Slack: "slack" };
const NOTIF_DB_TO_UI = { email: "E-mail", sms: "SMS", push: "Push", slack: "Slack" };
const mapDb = (r) => ({
  id: r.id, nom: r.nom, veilleId: r.veille_id, condition: r.condition,
  motsCles: r.mots_cles || [], canaux: r.canaux || [],
  notif: (r.notif || []).map((n) => NOTIF_DB_TO_UI[n] || n),
  seuil: seuilFromDb(r), active: r.active, declenchements: r.declenchements || 0,
});

export default function Alertes() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { veilles, getVeille } = useVeilles();
  const { org } = useSession();
  const [alertes, setAlertes] = useState([]);

  // Alertes RÉELLES (table alerts, filtrée par la RLS) — plus aucune donnée de démonstration.
  useEffect(() => {
    let actif = true;
    supabase.from("alerts").select("*").order("created_at", { ascending: false })
      .then(({ data }) => { if (actif) setAlertes((data || []).map(mapDb)); });
    return () => { actif = false; };
  }, []);

  // form modal
  const [editId, setEditId] = useState(null);
  const [nom, setNom] = useState("");
  const [veilleAlerte, setVeilleAlerte] = useState((veilles[0] || {}).id);
  const [condition, setCondition] = useState("");
  const [motsCles, setMotsCles] = useState([]);
  const [nouveauMot, setNouveauMot] = useState("");
  const [seuil, setSeuil] = useState("Dès la première mention");
  const [canaux, setCanaux] = useState(["presse", "reseaux"]);
  const [notif, setNotif] = useState(["E-mail"]);

  async function toggle(id) {
    const cur = alertes.find((a) => a.id === id);
    if (!cur) return;
    const active = !cur.active;
    setAlertes((prev) => prev.map((a) => (a.id === id ? { ...a, active } : a)));
    const { error } = await supabase.from("alerts").update({ active }).eq("id", id);
    if (error) {
      setAlertes((prev) => prev.map((a) => (a.id === id ? { ...a, active: cur.active } : a)));
      toast({ title: "Modification impossible", description: error.message, status: "error", duration: 3500 });
      return;
    }
    toast({ title: active ? `Alerte « ${cur.nom} » activée` : `Alerte « ${cur.nom} » désactivée`, status: active ? "success" : "info", duration: 2500 });
  }
  function toggleIn(list, set, id) {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }
  function ajouterMot() {
    const m = nouveauMot.trim();
    if (m && !motsCles.includes(m)) setMotsCles((p) => [...p, m]);
    setNouveauMot("");
  }

  function ouvrirCreation() {
    setEditId(null);
    setNom(""); setVeilleAlerte((veilles[0] || {}).id); setCondition("");
    setMotsCles([]); setNouveauMot("");
    setSeuil("Dès la première mention"); setCanaux(["presse", "reseaux"]); setNotif(["E-mail"]);
    onOpen();
  }
  function ouvrirEdition(a) {
    setEditId(a.id);
    setNom(a.nom); setVeilleAlerte(a.veilleId || (veilles[0] || {}).id); setCondition(a.condition || "");
    setMotsCles(a.motsCles || []); setNouveauMot("");
    setSeuil(a.seuil); setCanaux(a.canaux); setNotif(a.notif);
    onOpen();
  }
  async function enregistrer() {
    const payload = {
      veille_id: veilleAlerte || null,
      nom: nom || "Nouvelle alerte",
      condition: condition || "Un signal important est détecté",
      mots_cles: motsCles,
      canaux,
      notif: notif.map((n) => NOTIF_UI_TO_DB[n] || String(n).toLowerCase()),
      ...seuilToDb(seuil),
    };
    if (editId) {
      const { data, error } = await supabase.from("alerts").update(payload).eq("id", editId).select("*").maybeSingle();
      if (error) return toast({ title: "Enregistrement impossible", description: error.message, status: "error", duration: 4000, isClosable: true });
      setAlertes((p) => p.map((a) => (a.id === editId ? mapDb(data) : a)));
      toast({ title: "Alerte mise à jour", description: nom, status: "success", duration: 2500 });
    } else {
      const { data, error } = await supabase.from("alerts").insert({ ...payload, organisation_id: (org && org.id) || null, active: true }).select("*").maybeSingle();
      if (error) return toast({ title: "Création impossible", description: error.message, status: "error", duration: 4000, isClosable: true });
      setAlertes((p) => [mapDb(data), ...p]);
      toast({ title: "Alerte créée", description: nom || "Nouvelle alerte", status: "success", duration: 3000 });
    }
    onClose();
  }
  async function supprimer(a) {
    const { error } = await supabase.from("alerts").delete().eq("id", a.id);
    if (error) return toast({ title: "Suppression impossible", description: error.message, status: "error", duration: 3500, isClosable: true });
    setAlertes((p) => p.filter((x) => x.id !== a.id));
    toast({ title: `Alerte « ${a.nom} » supprimée`, status: "info", duration: 2500 });
  }

  // On ne montre que les alertes des veilles auxquelles l'utilisateur a accès
  // (un analyste ne doit jamais voir une alerte liée à une veille non autorisée).
  const alertesVisibles = alertes.filter((a) => !a.veilleId || getVeille(a.veilleId));

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} mb="28px" gap="16px">
        <Flex align="center">
          <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
          <Box>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Alertes flash</Text>
            <Text fontSize="md" color="gray.700" mt="4px">Définissez les signaux à ne pas manquer sur vos veilles.</Text>
          </Box>
        </Flex>
        <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" size="lg" leftIcon={<Icon as={FaPlus} />} onClick={ouvrirCreation}>Nouvelle alerte</Button>
      </Flex>

      {/* Transparence : la config est enregistrée, mais l'évaluation moteur n'est pas encore active. */}
      <Flex align="center" gap="12px" mb="22px" p="14px 16px" bg="blue.50" borderRadius="14px">
        <Icon as={FaBolt} color="blue.400" flexShrink={0} />
        <Text fontSize="sm" color="blue.700">Vos règles d'alerte sont enregistrées. Le déclenchement automatique et l'envoi des notifications seront activés très prochainement.</Text>
      </Flex>

      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }}>
        <Flex align="center" mb="18px" px="4px">
          <Box w="6px" h="42px" borderRadius="full" bg="teal.300" me="14px" flexShrink={0} />
          <Box>
            <Text fontSize="lg" color={textColor} fontWeight="bold">Mes alertes</Text>
            <Text fontSize="sm" color="gray.700">Une alerte vous prévient dès qu'un signal important apparaît sur une veille.</Text>
          </Box>
        </Flex>

        <Flex direction="column" gap="14px">
          {alertesVisibles.map((a) => {
            const v = a.veilleId ? getVeille(a.veilleId) : null;
            return (
              <Box key={a.id} bg="white" borderRadius="16px" p={{ base: "18px", md: "22px" }} boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                <Flex direction={{ base: "column", md: "row" }} align={{ md: "flex-start" }} w="100%" gap="18px">
                  <Flex w="46px" h="46px" borderRadius="14px" bg={a.active ? "teal.50" : "gray.100"} align="center" justify="center" flexShrink={0}>
                    <Icon as={FaBolt} color={a.active ? "teal.500" : "gray.400"} h="18px" w="18px" />
                  </Flex>
                  <Box flex="1" minW="0">
                    <Flex align="center" gap="10px" mb="4px" wrap="wrap">
                      <Text fontSize="md" fontWeight="bold" color="gray.800">{a.nom}</Text>
                      {v && <Badge bg="teal.50" color="teal.600" borderRadius="6px" textTransform="none" fontWeight="bold">Veille : {v.client || v.nom}</Badge>}
                      {a.declenchements > 0 && (
                        <Badge colorScheme="red" borderRadius="8px"><Icon as={FaBolt} fontSize="9px" mr="4px" />{a.declenchements} déclenchements</Badge>
                      )}
                    </Flex>
                    <Text fontSize="sm" color="gray.700" mb="16px">Vous êtes prévenu quand : <Text as="span" fontWeight="bold" color="gray.800">{a.condition}</Text>.</Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing="16px">
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="7px">Mots-clés surveillés</Text>
                        <Flex gap="6px" wrap="wrap">
                          {(a.motsCles || []).map((m) => (<Badge key={m} bg="gray.100" color="gray.700" borderRadius="6px" textTransform="none" fontWeight="medium" px="8px" py="3px">{m}</Badge>))}
                          {(!a.motsCles || a.motsCles.length === 0) && <Text fontSize="xs" color="gray.400">—</Text>}
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="7px">Médias surveillés</Text>
                        <Flex align="center" gap="8px" wrap="wrap">
                          {a.canaux.map((c) => (
                            <Flex key={c} align="center" gap="6px" bg="gray.100" borderRadius="8px" px="8px" py="4px">
                              <Icon as={canal(c).icon} color="gray.500" fontSize="12px" />
                              <Text fontSize="xs" color="gray.700" fontWeight="medium">{canal(c).label}</Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="7px">Se déclenche</Text>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800" mb="10px">{a.seuil}</Text>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" mb="7px">Vous êtes prévenu par</Text>
                        <Flex gap="6px" wrap="wrap">
                          {a.notif.map((n) => (<Badge key={n} bg="gray.100" color="gray.700" borderRadius="6px" textTransform="none" fontWeight="medium">{n}</Badge>))}
                        </Flex>
                      </Box>
                    </SimpleGrid>
                  </Box>
                  <Flex align="center" gap="10px" flexShrink={0}>
                    <Switch colorScheme="teal" isChecked={a.active} onChange={() => toggle(a.id)} />
                    <Text fontSize="sm" fontWeight="bold" color={a.active ? "gray.800" : "gray.400"}>{a.active ? "Active" : "Inactive"}</Text>
                  </Flex>
                </Flex>

                {/* Actions */}
                <Divider my="16px" borderColor="gray.100" />
                <Flex gap="10px" justify="flex-end">
                  <Button size="sm" variant="outline" borderColor="gray.200" color="gray.700" borderRadius="10px" leftIcon={<Icon as={FaPen} fontSize="11px" />} _hover={{ bg: "gray.50" }} onClick={() => ouvrirEdition(a)}>Modifier</Button>
                  <Button size="sm" variant="outline" borderColor="red.200" color="red.500" borderRadius="10px" leftIcon={<Icon as={FaTrash} fontSize="11px" />} _hover={{ bg: "red.50" }} onClick={() => supprimer(a)}>Supprimer</Button>
                </Flex>
              </Box>
            );
          })}
          {alertesVisibles.length === 0 && (
            <Flex direction="column" align="center" py="40px">
              <Text fontWeight="bold" color="gray.700">Aucune alerte pour l'instant</Text>
              <Text fontSize="sm" color="gray.500" mt="4px">Créez une alerte pour être prévenu des signaux importants.</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      {/* Modal création / édition */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(3px)" />
        <ModalContent borderRadius="20px" overflow="hidden">
          <Flex align="center" px="26px" pt="26px" pb="18px">
            <Box w="6px" h="46px" borderRadius="full" bg="teal.300" me="16px" flexShrink={0} />
            <Box>
              <Text fontSize="xl" fontWeight="bold" color="gray.800" lineHeight="1.1">{editId ? "Modifier l'alerte" : "Créer une alerte flash"}</Text>
              <Text fontSize="sm" color="gray.600" mt="2px">Choisissez la veille, les mots-clés et comment être prévenu.</Text>
            </Box>
          </Flex>
          <ModalCloseButton top="22px" right="20px" />
          <ModalBody px="26px">
            <Stack spacing="18px">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Sur quelle veille ?</FormLabel>
                <Select value={veilleAlerte} onChange={(e) => setVeilleAlerte(e.target.value)} borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200">
                  {veilles.map((v) => (<option key={v.id} value={v.id}>{v.client || v.nom}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Nom de l'alerte</FormLabel>
                <Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex. : Bad buzz produit" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Quand faut-il vous prévenir ?</FormLabel>
                <Input value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="Ex. : une mention de rappel ou d'incident sur la marque" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Mots-clés à surveiller</FormLabel>
                <Wrap mb="10px">
                  {motsCles.map((m) => (
                    <WrapItem key={m}>
                      <Tag borderRadius="full" bg="gray.100" color="gray.800" size="lg">
                        <TagLabel>{m}</TagLabel>
                        <TagCloseButton onClick={() => setMotsCles((p) => p.filter((x) => x !== m))} />
                      </Tag>
                    </WrapItem>
                  ))}
                  {motsCles.length === 0 && <Text fontSize="sm" color="gray.500">Aucun mot-clé pour l'instant.</Text>}
                </Wrap>
                <Flex gap="10px">
                  <Input value={nouveauMot} onChange={(e) => setNouveauMot(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouterMot(); } }} placeholder="Ajouter un mot-clé et appuyer sur Entrée" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                  <Button onClick={ajouterMot} variant="outline" borderColor="gray.300" color="gray.800" borderRadius="12px" leftIcon={<Icon as={FaPlus} />}>Ajouter</Button>
                </Flex>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Médias à surveiller</FormLabel>
                <Flex wrap="wrap" gap="10px">
                  {CANAUX.map((c) => {
                    const on = canaux.includes(c.id);
                    return (
                      <Flex key={c.id} onClick={() => toggleIn(canaux, setCanaux, c.id)} cursor="pointer" align="center" gap="8px" px="14px" py="9px" borderRadius="full" border="2px solid" borderColor={on ? "teal.300" : "gray.200"} bg={on ? "teal.50" : "white"} transition="all .15s">
                        <Icon as={c.icon} color={on ? "teal.500" : "gray.400"} fontSize="13px" />
                        <Text fontSize="sm" fontWeight="bold" color={on ? "gray.800" : "gray.600"}>{c.label}</Text>
                      </Flex>
                    );
                  })}
                </Flex>
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">Se déclenche</FormLabel>
                  <Select value={seuil} onChange={(e) => setSeuil(e.target.value)} borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200">
                    <option>Dès la première mention</option>
                    <option>Plus de 10 mentions par heure</option>
                    <option>Plus de 25 mentions par heure</option>
                    <option>Plus de 50 mentions par heure</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">Vous prévenir par</FormLabel>
                  <Flex wrap="wrap" gap="10px">
                    {["E-mail", "SMS", "Push", "Slack"].map((n) => {
                      const on = notif.includes(n);
                      return (
                        <Flex key={n} onClick={() => toggleIn(notif, setNotif, n)} cursor="pointer" px="14px" py="9px" borderRadius="full" border="2px solid" borderColor={on ? "teal.300" : "gray.200"} bg={on ? "teal.50" : "white"} transition="all .15s">
                          <Text fontSize="sm" fontWeight="bold" color={on ? "gray.800" : "gray.600"}>{n}</Text>
                        </Flex>
                      );
                    })}
                  </Flex>
                </FormControl>
              </SimpleGrid>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" borderRadius="12px" mr="10px" onClick={onClose}>Annuler</Button>
            <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" onClick={enregistrer}>{editId ? "Enregistrer" : "Créer l'alerte"}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
